// Client side of the typography CSS-variable contract. The server renders
// `--jet-type-<role>-size | -line-height | -weight | -transform` onto
// `html.jet-public` (see server/utils/typography/renderTypographyCss.js) from the
// values Marketing publishes at /dashboard/typography.
//
// Everything that should follow the dashboard reads these variables — the MUI
// theme variants (theme/typography.js), form overrides, and `typeRole()` in sx
// for elements that aren't a Typography variant (nav links, hero CTA).
//
// The fallbacks are TODAY's static look. They only apply where the variables are
// undefined — admin screens (no `jet-public` class) and the rare case the
// injected CSS is missing — so admin renders exactly as before.

// Invisible marker naming the role an element's text follows. It has no
// visual effect; the dashboard preview reads it to label text ("H1 · 52px")
// so Marketing can see which setting controls what (components/typography/
// roleLabels.js registers it as non-inherited inside the preview frame).
export const ROLE_MARKER = '--jet-type-role';

export const typeVar = (role, prop, fallback) =>
  fallback === undefined || fallback === null
    ? `var(--jet-type-${role}-${prop})`
    : `var(--jet-type-${role}-${prop}, ${fallback})`;

// textTransform defaults to `inherit` (not `none`) so an element inside an
// uppercase container keeps behaving as it did before the variables existed.
const fb = (fontSize, lineHeight, fontWeight, textTransform = 'inherit') => ({
  fontSize,
  lineHeight,
  fontWeight,
  textTransform,
});

export const ROLE_FALLBACKS = {
  h1: fb('2.5rem', 1.25, 800),
  h2: fb('2rem', 1.3333, 800),
  h3: fb('1.5rem', 1.5, 700),
  h4: fb('1.25rem', 1.5, 700),
  'h5-h6': fb('1.125rem', 1.5, 700),
  body: fb('1.0625rem', 1.5, 400),
  'body-small': fb('0.9375rem', 1.5714, 400),
  button: fb('0.9375rem', 1.7143, 700, 'capitalize'),
  'hero-headline': fb('2.5rem', 1.2, 800),
  'hero-subheadline': fb('1.125rem', 1.5, 400),
  'hero-cta': fb('0.9375rem', 1.7143, 700, 'uppercase'),
  'nav-main': fb('0.9375rem', 1.7143, 700),
  'nav-utility': fb('0.875rem', 1.4, 500),
  'footer-heading': fb('1.125rem', 1.5556, 700),
  'footer-text': fb('1.0625rem', 1.5, 400),
  'footer-legal': fb('0.75rem', 1.6, 400),
  'form-label': fb('1.0625rem', 1.4375, 400),
  'form-input': fb('1.0625rem', 1.4375, 400),
  'form-helper': fb('0.75rem', 1.5, 400),
};

// sx/style object for an element that should follow a dashboard role.
// `only` limits which properties are set (e.g. ['fontSize', 'fontWeight']);
// the role marker is always included.
export function typeRole(role, only) {
  const f = ROLE_FALLBACKS[role] || {};
  const all = {
    fontSize: typeVar(role, 'size', f.fontSize),
    lineHeight: typeVar(role, 'line-height', f.lineHeight),
    fontWeight: typeVar(role, 'weight', f.fontWeight),
    textTransform: typeVar(role, 'transform', f.textTransform),
  };
  const picked = only ? Object.fromEntries(only.map((k) => [k, all[k]])) : all;
  return { ...picked, [ROLE_MARKER]: role };
}

// Inline-style object that DEFINES the variables from editor state — used by the
// dashboard preview so it renders through the exact same contract as the site.
// `device` picks which size applies ('desktop' | 'mobile'); invalid in-progress
// input falls back to the role default so the preview never collapses.
export function typeRoleStyleVars(roles, device, defaults = {}) {
  const style = {};
  Object.keys(defaults).forEach((key) => {
    const v = roles?.[key] || {};
    const d = defaults[key];
    const num = (x, dflt) => (Number.isFinite(Number(x)) && x !== '' ? Number(x) : dflt);
    const px = device === 'mobile' ? num(v.sizeMobile, d.sizeMobile) : num(v.sizeDesktop, d.sizeDesktop);
    style[`--jet-type-${key}-size`] = `${px / 16}rem`;
    style[`--jet-type-${key}-line-height`] = num(v.lineHeight, d.lineHeight);
    style[`--jet-type-${key}-weight`] = num(v.weight, d.weight);
    style[`--jet-type-${key}-transform`] = v.textTransform || d.textTransform;
  });
  return style;
}
